import './MetricGauge.css';

const RADIUS        = 34;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/* ─── Parsing de la valeur ────────────────────────────────── */

function detectValueType(raw) {
  if (!raw) return { type: 'text', display: 'N/A', percent: null };

  // Pourcentage : "87%" ou "87.3 %"
  const pct = raw.match(/^([\d.]+)\s*%/);
  if (pct) {
    return { type: 'percent', percent: Math.min(parseFloat(pct[1]), 100), display: raw };
  }

  // Ratio : "14.2 GB / 16 GB" ou "199 Go (49%)"
  const parenPct = raw.match(/^([\d.]+\s*[A-Za-z]*)\s*\((\d+)%\)/);
  if (parenPct) {
    return { type: 'percent', percent: Math.min(parseFloat(parenPct[2]), 100), display: raw, bigLabel: parenPct[2] + '%', subLabel: parenPct[1] };
  }

  const ratio = raw.match(/^([\d.]+)\s*([A-Za-z]*)\s*\/\s*([\d.]+)\s*([A-Za-z]*)/);
  if (ratio) {
    const used  = parseFloat(ratio[1]);
    const total = parseFloat(ratio[3]);
    const unit  = ratio[2] || ratio[4] || '';
    return { type: 'ratio', percent: total > 0 ? Math.min((used / total) * 100, 100) : 0, used, total, unit, display: raw };
  }

  // Nombre avec unité : "33 clients", "3 handlers", "48 workers", "2.8 Go"
  const numUnit = raw.match(/^([\d.]+)\s+(.+)$/);
  if (numUnit) {
    return { type: 'number', number: numUnit[1], unit: numUnit[2], display: raw };
  }

  // Nombre seul
  const num = raw.match(/^([\d.]+)$/);
  if (num) {
    return { type: 'number', number: num[1], unit: '', display: raw };
  }

  // Texte : "OK", "Stats FPM", etc.
  return { type: 'text', display: raw, percent: null };
}

function alerteToColorClass(alerte) {
  const l = parseInt(alerte, 10);
  if (l === 4) return 'crit';
  if (l === 3) return 'degrade';
  if (l === 2) return 'warn';
  return 'ok';
}

function alerteToColor(alerte) {
  const l = parseInt(alerte, 10);
  if (l === 4) return '#dc2626';
  if (l === 3) return '#ea580c';
  if (l === 2) return '#d97706';
  return '#16a34a';
}

/* ─── Gauge circulaire ────────────────────────────────────── */

function CircularGauge({ percent, colorClass, label, sub }) {
  const filled = (percent / 100) * CIRCUMFERENCE;
  const offset = CIRCUMFERENCE - filled;

  return (
    <svg className="gauge-svg" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={RADIUS} className="gauge-track" />
      <circle
        cx="40" cy="40" r={RADIUS}
        className={`gauge-fill gauge-fill-${colorClass}`}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform="rotate(-90 40 40)"
      />
      <text x="40" y={sub ? "36" : "40"} className="gauge-text" style={{ fontSize: sub ? 11 : 13 }}>
        {label || `${Math.round(percent)}%`}
      </text>
      {sub && (
        <text x="40" y="50" style={{ fontSize: 8, fill: '#6b7280', textAnchor: 'middle' }}>
          {sub}
        </text>
      )}
    </svg>
  );
}

/* ─── Barre ratio ─────────────────────────────────────────── */

function RatioDisplay({ parsed, color }) {
  return (
    <div className="metric-ratio-wrap">
      <div className="metric-ratio-value" style={{ color }}>
        {parsed.used}<span style={{ fontSize: '0.8rem', fontWeight: 600 }}> {parsed.unit}</span>
      </div>
      <div className="metric-ratio-unit">sur {parsed.total} {parsed.unit}</div>
      <div className="metric-ratio-bar">
        <div className="metric-ratio-fill" style={{ width: `${parsed.percent}%`, background: color }} />
      </div>
    </div>
  );
}

/* ─── Valeur numérique brute ──────────────────────────────── */

function NumberDisplay({ parsed, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="metric-raw-value" style={{ color }}>{parsed.number}</div>
      {parsed.unit && <div className="metric-raw-unit">{parsed.unit}</div>}
    </div>
  );
}

/* ─── Texte (OK, Stats FPM…) ─────────────────────────────── */

function TextDisplay({ display, color }) {
  const isShort = display.length <= 6;
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        className="metric-raw-value"
        style={{
          color,
          fontSize: isShort ? '1.4rem' : '0.95rem',
          fontWeight: isShort ? 900 : 700,
        }}
      >
        {display}
      </div>
    </div>
  );
}

/* ─── MetricGauge ─────────────────────────────────────────── */

function MetricGauge({ metric, onClick }) {
  const name       = metric.intitule || metric.metric_name;
  const colorClass = alerteToColorClass(metric.alerte);
  const color      = alerteToColor(metric.alerte);
  const alerteLevel= parseInt(metric.alerte, 10);
  const parsed     = detectValueType(metric.value);

  function renderCenter() {
    if (parsed.type === 'percent') {
      return (
        <CircularGauge
          percent={parsed.percent}
          colorClass={colorClass}
          label={parsed.bigLabel}
          sub={parsed.subLabel}
        />
      );
    }

    if (parsed.type === 'ratio') {
      return <RatioDisplay parsed={parsed} color={color} />;
    }

    if (parsed.type === 'number') {
      return <NumberDisplay parsed={parsed} color={color} />;
    }

    return <TextDisplay display={parsed.display} color={color} />;
  }

  return (
    <div
      className={`metric-gauge-card alerte-${alerteLevel}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      aria-label={`${name}: ${parsed.display}`}
    >
      <div className="metric-center">
        {renderCenter()}
      </div>

      <div className="metric-footer">
        <div className="metric-gauge-name">{name}</div>
        {(parsed.type === 'ratio' || parsed.type === 'percent') && parsed.display && (
          <div className="metric-gauge-raw">{parsed.display}</div>
        )}
      </div>
    </div>
  );
}

export default MetricGauge;
